import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGetClientFunctionRequest } from "shared/request/myHealthyAdvantageApi";
import ViewLoading from "shared/UI/Spinners/ViewLoading";
import Button from "shared/UI/Buttons/Button";
import { useNotifyModalContext } from "shared/core/state/NotifyModalContext/NotifyModalContext";
import { PageHeader } from "../../UIPalette/components/PageHeader/PageHeader";
import { ClientTypeAhead } from "../../UIPalette/components/ClientTypeAhead/ClientTypeAhead";
import { getHubTypeModules } from "../ClientManagement/View/clientModules";
import { HubTypeLinksSection, HubTypeLinksSectionHandle } from "./Components/HubTypeLinksSection/HubTypeLinksSection";

interface HubSitesLocationState {
  clientId?: string;
}

export const HubSites = () => {
  const { state } = useLocation();
  const clientId = (state as HubSitesLocationState)?.clientId;
  const { t } = useTranslation();
  const { modalNotify } = useNotifyModalContext();

  const [selectedClientId, setSelectedClientId] = useState<string | undefined>(clientId);
  const [hasResolvedPresetClient, setHasResolvedPresetClient] = useState(!clientId);
  const [isSaving, setIsSaving] = useState(false);
  const [dirtyModules, setDirtyModules] = useState<Record<string, boolean>>({});

  const { data: selectedClient, mutate: mutateSelectedClient } = useGetClientFunctionRequest(
    { clientId: selectedClientId },
    { shouldFetch: !!selectedClientId }
  );

  useEffect(() => {
    if (clientId) {
      mutateSelectedClient();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (clientId && selectedClient && !hasResolvedPresetClient) {
      setHasResolvedPresetClient(true);
    }
  }, [clientId, selectedClient, hasResolvedPresetClient]);

  useEffect(() => setDirtyModules({}), [selectedClientId]);

  const hubTypeModules = useMemo(() => getHubTypeModules(selectedClient?.modules), [selectedClient]);
  const sectionRefs = useRef<Record<string, HubTypeLinksSectionHandle | null>>({});

  const handleDirtyChange = useCallback(
    (moduleId: string, isDirty: boolean) => setDirtyModules((prev) => (prev[moduleId] === isDirty ? prev : { ...prev, [moduleId]: isDirty })),
    []
  );

  const isAnyDirty = Object.values(dirtyModules).some(Boolean);

  const handleReset = () => Object.values(sectionRefs.current).forEach((section) => section?.reset());

  const handleSave = async () => {
    const sections = hubTypeModules
      .map((module) => sectionRefs.current[module.id])
      .filter((section): section is HubTypeLinksSectionHandle => !!section);
    const isValid = sections.map((section) => section.validate()).every(Boolean);
    if (!isValid) {
      modalNotify("error", t("hubSites.validationErrorTitle"), t("hubSites.validationErrorMessage"));
      return;
    }

    setIsSaving(true);
    try {
      await Promise.all(sections.map((section) => section.save()));
      modalNotify("success", t("hubSites.successTitle"), t("hubSites.successMessage"));
    } catch {
      modalNotify("error", t("hubSites.failedTitle"), t("hubSites.failedMessage"));
    } finally {
      setIsSaving(false);
    }
  };

  if (!hasResolvedPresetClient) {
    return <ViewLoading label={t("hubSites.loading")} />;
  }

  return (
    <div>
      <PageHeader title={t("hubSites.title")} subtitle={t("hubSites.subtitle")} />

      <div className="mt-8">
        <ClientTypeAhead
          className="w-1/2"
          aria-label={t("hubSites.clientSelection.label")}
          placeholder={t("hubSites.clientSelection.label")}
          onClientSelected={(client) => {
            setSelectedClientId(client?.id);
          }}
          initialClient={selectedClient}
        />
      </div>

      {selectedClient && hubTypeModules.length === 0 && <p className="mt-8">{t("hubSites.noHubTypeModulesMessage")}</p>}

      {selectedClient &&
        hubTypeModules.map((module) => (
          <HubTypeLinksSection
            key={`${selectedClient.id}-${module.id}`}
            ref={(section) => (sectionRefs.current[module.id] = section)}
            clientId={selectedClient.id}
            clientCode={selectedClient.code}
            module={module}
            onDirtyChange={handleDirtyChange}
          />
        ))}

      <div className="mt-8 flex gap-4">
        <Button className="max-w-[200px]" variant="secondary" text={t("hubSites.resetButton")} disabled={!isAnyDirty} onClick={handleReset} />
        <Button
          className="max-w-[200px]"
          text={t("hubSites.saveButton")}
          disabled={!selectedClient || hubTypeModules.length === 0 || isSaving || !isAnyDirty}
          onClick={handleSave}
        />
      </div>
    </div>
  );
};
